import React from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import type { SchemaType } from '$util/schema';
import { CmsLinkSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import type { ContentComponent } from '$cms/ContentComponent';
import { Card } from '$src-components/atoms/Card';
import { ContentComponentSection } from '$src-components/atoms/ContentComponentSection';
import { Col, Row } from '$src-components/atoms/Grid';
import { ActiveExperiments } from '$util/abTest/experiments';
import { useActiveExperiment } from '$util/abTest';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import { BrandedIconListCard } from '@/components/molecules/BrandedIconListCard';
import { Img } from '../../atoms/Img';
import styles from './index.module.scss';

export const WeHaveYourBackSchema = {
  translatedEntities: z.object({
    heading: CmsRichTextSchema,
    card1Content: CmsRichTextSchema,
    card2Content: CmsRichTextSchema,
    card3Content: CmsRichTextSchema,
    card4Content: CmsRichTextSchema,
    card5Content: CmsRichTextSchema,
    icon1Id: CmsShortTextSchema.optional(),
    icon2Id: CmsShortTextSchema.optional(),
    icon3Id: CmsShortTextSchema.optional(),
    icon4Id: CmsShortTextSchema.optional(),
    text1Content: CmsShortTextSchema.optional(),
    text2Content: CmsShortTextSchema.optional(),
    text3Content: CmsShortTextSchema.optional(),
    text4Content: CmsShortTextSchema.optional(),
    sentence1Content: CmsShortTextSchema.optional(),
    sentence2Content: CmsShortTextSchema.optional(),
    sentence3Content: CmsShortTextSchema.optional(),
    sentence4Content: CmsShortTextSchema.optional(),
    speechBubbleText: CmsShortTextSchema.optional(),
    modalSpeechBubbleText: CmsShortTextSchema.optional(),
    link: CmsLinkSchema.optional(),
  }),
};

export type WeHaveYourBackTranslatedEntities = SchemaType<
  (typeof WeHaveYourBackSchema)['translatedEntities']
>;

/**
 * https://www.notion.so/fixter/WeHaveYourBack-97d2dd4b70ab4257ae66a444ecca01ef?pvs=4
 */

export const WeHaveYourBack: ContentComponent<WeHaveYourBackTranslatedEntities> = function WeHaveYourBack({
  translatedEntities,
  className,
  ...props
}): JSX.Element {
  const {
    heading,
    card1Content,
    card2Content,
    card3Content,
    card4Content,
    card5Content,
    text1Content,
    text2Content,
    text3Content,
    text4Content,
    sentence1Content,
    sentence2Content,
    sentence3Content,
    sentence4Content,
    icon1Id,
    icon2Id,
    icon3Id,
    icon4Id,
    speechBubbleText,
    modalSpeechBubbleText,
    link,
  } = translatedEntities;

  const RevisionAndVidange = useActiveExperiment(ActiveExperiments.RevisionAndVidangeLandingPageFR, 'B');
  const CarServiceNewLandingPage = useActiveExperiment(ActiveExperiments.NewHeroAndUSPsLandingPageUK, 'B');

  // @NOTE the following conditional render is only used for the purposes of the A/B test
  // @TODO once test is over and implementation is needed, rever the below conditional render and add the BrandedIconListCard component to the component factory
  return RevisionAndVidange || CarServiceNewLandingPage ? (
    <BrandedIconListCard
      id={WeHaveYourBack.displayName}
      translatedEntities={{
        itemIcons: {
          1: icon1Id,
          2: icon2Id,
          3: icon3Id,
          4: icon4Id,
        },
        itemText: {
          1: text1Content,
          2: text2Content,
          3: text3Content,
          4: text4Content,
        },
        itemSentence: {
          1: sentence1Content,
          2: sentence2Content,
          3: sentence3Content,
          4: sentence4Content,
        },
        speechBubbleText,
        modalSpeechBubbleText,
        link,
      }}
    />
  ) : (
    <ContentComponentSection
      className={classNames(WeHaveYourBack.displayName, styles.component, className)}
      {...props}
    >
      <Row className={styles.row}>
        <div className={styles.darkTracks}>&nbsp;</div>
        <Col className={styles.col}>
          <div className={styles.colDarkTracks}>&nbsp;</div>
          <Img
            className={styles.fixie}
            provider="cloudinary"
            src="/hero-landing/Fixie_left_lzar9u.png"
            alt="Mascot"
            width={502}
            height={459}
          />
          <div className={styles.container}>
            <HeadingLevels semanticLevel={3} styleLevel={3} className={styles.heading}>
              {renderRichTextContent(heading.content)}
            </HeadingLevels>
            <div className={styles.cards}>
              <Card className={styles.card}>{renderRichTextContent(card1Content.content)}</Card>
              <Card className={styles.card}>{renderRichTextContent(card2Content.content)}</Card>
              <Card className={styles.card}>{renderRichTextContent(card3Content.content)}</Card>
              <Card className={styles.card}>{renderRichTextContent(card4Content.content)}</Card>
              <Card className={styles.card}>{renderRichTextContent(card5Content.content)}</Card>
            </div>
          </div>
        </Col>
      </Row>
    </ContentComponentSection>
  );
};
WeHaveYourBack.displayName = 'WeHaveYourBack';
WeHaveYourBack.schema = WeHaveYourBackSchema;
